import { Save as SaveIcon } from '@mui/icons-material';
import { Box, Fab, MenuItem, useTheme } from "@mui/material";
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { UseQueryResult } from "react-query";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useObjectDefaults } from "../../queries/object-defaults/UseObjectDefaults";
import { useSaveObjectDefaults } from "../../queries/object-defaults/UseSaveObjectDefaults";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { DefaultTagAssociationTypeEnum, IObjectDefaultsModel } from "../../services/defaults-data-service/Models";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import { CheckList, CheckListItemType } from "../check-list/CheckList";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../loading-zone/LoadingZone";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSelect from "../side-sheet/side-sheet-select/SideSheetSelect";
import SideSheetSwitch from "../side-sheet/side-sheet-switch/SideSheetSwitch";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import useObjectDefaultsState from "./ObjectDefaultsState";
import { contains } from '../../common/utilities/Contains';

export type ObjectDefaultsProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type ObjectDefaultsType = {
    executeAction: (action: BaseActionEnum) => void;
};

function ObjectDefaults({
    visible,
    setViewOptionState
}: ObjectDefaultsProps, ref: Ref<unknown> | undefined) {
    const routeContext = useRouteContext();
    const theme = useTheme();
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const [ isSaving, setIsSaving ] = useState<boolean>(false);

    const { data, status, refetch } = useQueryNotifications(useObjectDefaults)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject
    }) as UseQueryResult<IObjectDefaultsModel | undefined>;

    const { mutate: saveDefaults } = useQueryNotifications(useSaveObjectDefaults)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject,
        onSuccess: () => {
            acknowledge(t('defaults.defaultsSaved'));
            setIsSaving(false);
        }
    })

    const {
        state,
        setData,
        setMode,
        setTatFilterValue,
        setViewSelected,
        reset,
        resetOptions,
        updateTatDefault
    } = useObjectDefaultsState();

    const {
        mode,
        tatsFilterValue,
        viewSelected
    } = state.options;

    const onReset = () => {
        resetOptions();
    }

    useImperativeHandle<unknown, ObjectDefaultsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    async function executeAction(action: BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.Refresh:
                if (state.isDirty) {
                    await getUserConfirmation(true);
                }
            break;
        }
    }

    useEffect(() => {
        if (data) {
            setData(data);
        }
    }, [
        data,
        setData,
    ])

    const onModeChanged = (value: string) => {
        const newMode = Number(value) as DefaultTagAssociationTypeEnum;

        setMode(newMode);
    }

    const onTatClicked = (item: CheckListItemType) => {
        updateTatDefault(item);
    }

    const readOnly = data?.canModifyDevice !== true;

    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState && setViewOptionState(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState
    ])

    async function getUserConfirmation(refresh?: boolean) {
        const ok = (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: refresh === true
                ? t("defaults.refreshDefaultsConfirmationDialogText", {objectType: eLynxObjectTypeEnum[routeContext?.contextObject?.objectType as eLynxObjectTypeEnum]})
                : t("defaults.cancelDefaultsConfirmationDialogText", {objectType: eLynxObjectTypeEnum[routeContext?.contextObject?.objectType as eLynxObjectTypeEnum]}),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";

        if (ok) {
            reset();
            refetch();
        }
        return ok;
    }

    const onSaveDefaults = async () => {
        if (data) {
            const updatedDefaults = {
                ...data,
                defaults: state.defaults
            };

            setIsSaving(true);  // Disable fab
            saveDefaults(updatedDefaults);
        }
    }

    return (
        <>
            {visible &&
                <LoadingZone isLoading={status === "loading"}>
                    <ContentContainer>
                        <ContentSection>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1,
                                    paddingLeft: theme.spacing(1),
                                    paddingTop: theme.spacing(1),
                                }}>
                                {t("defaults.availableColumns")}:
                                <Box sx={{
                                    flexGrow: 1,
                                    opacity: readOnly ? 0.5 : 1,
                                    pointerEvents: readOnly ? 'none' : 'inherit'
                                }}>
                                <CheckList
                                    items={state.availableTats}
                                    onItemClick={onTatClicked}
                                    filter={
                                        (item: CheckListItemType) => {
                                            return (!viewSelected ||
                                                item.checked) &&
                                                item.key.startsWith(`${mode as number}:`) &&
                                                !!contains(item.displayText, tatsFilterValue);
                                        }
                                    }
                                />
                                </Box>
                            </Box>

                            {!readOnly &&
                                <Fab color="secondary"
                                    id="submit"
                                    type="submit"
                                    name="update"
                                    disabled={!state.isDirty || isSaving}
                                    onClick={() => onSaveDefaults()}
                                    aria-label="update defaults"
                                    sx={{
                                        position: "absolute",
                                        bottom: theme.spacing(2),
                                        right: theme.spacing(1)
                                    }}
                                    >
                                    <SaveIcon />
                                </Fab>
                            }

                        </ContentSection>

                        <OptionsSideSheet onReset={() => onReset()}>
                            <SideSheetSelect
                                label={t("defaults.defaultTagsFor")}
                                value={mode.toString()}
                                onChange={onModeChanged}
                            >
                                <MenuItem value={DefaultTagAssociationTypeEnum.Graph.toString()}>
                                    {t("defaults.graph")}
                                </MenuItem>
                                <MenuItem value={DefaultTagAssociationTypeEnum.Mapping.toString()}>
                                    {t("defaults.mapping")}
                                </MenuItem>
                            </SideSheetSelect>
                            <SideSheetFilterInput
                                label={t("defaults.filterText")}
                                value={tatsFilterValue}
                                onChange={(value: string) => {
                                    setTatFilterValue(value);
                                }}
                            />
                            <SideSheetSwitch
                                label={t("defaults.viewSelected")}
                                checked={viewSelected}
                                onChange={(checked: boolean) => {
                                    setViewSelected(checked);
                                }}
                            />
                        </OptionsSideSheet>

                    </ContentContainer>
                </LoadingZone>
            }

            <UnsavedChangesPrompt
                hasChanges={state.isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    )
}

export default forwardRef(ObjectDefaults);